import { LicenseManager } from '@ag-grid-enterprise/core';
import * as angular from 'angular';
import { ILocationService } from 'angular';
import * as _ from 'lodash';
import { Auth } from '../../ng2/shared/auth/auth.service';
import nvClusterModule from '../features/landing/cluster/nv-cluster.module';
import { ToolbarsModule } from '../layout/toolbars/toolbars.module';
import nvSharedModelsModule from '../shared/models/nv.shared.models.module';
// tslint:disable-next-line:max-line-length
import {
  API_AUTH_FAILURE_MESSAGE,
  API_AUTH_INVALID_FORMAT,
  API_AUTH_MISSING_MESSAGE,
  IDLENESS_TIME_TO_SHOW_WARNING,
  IDLENESS_WARNING,
} from './../../ng2/shared/constants/auth/auth.constant';
import appCoreModule from './app.core.module';
import nvBuildsModule from './nv.builds.module';

// app definition

// tslint:disable-next-line:max-line-length
LicenseManager.setLicenseKey(
  'CompanyName=New Visions for Public Schools,LicensedApplication=New Visions Data Tools Portal,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=2,LicensedProductionInstancesCount=1,AssetReference=AG-009463,ExpiryDate=13_August_2021_[v2]_MTYyODgwOTIwMDAwMA==1967db81ed318e9aabe578b3e4d39feb',
);

agGrid.initialiseAgGridWithAngular1(angular);

const ng1AppModule = angular
  .module('nv-data-tools-portal', [
    // ng & vendor dependencies

    appCoreModule,
    nvBuildsModule,

    // load data models
    nvSharedModelsModule,

    // load filters mod
    'nv.shared.filters',

    // shared services
    'nv.shared.services',

    'nv.shared.error-handling',

    // load directives mods
    'nv.directives',

    // load components mods
    'nv.components',

    // features
    'nv.features',

    // load routes
    'nv.routes',

    // load cluster
    nvClusterModule,

    // load SDC
    'nv.sdcModule',

    // load single student
    'nv.singleStudent',

    // load school tools
    'nv.HelpDeskModule',
    'nv.SchoolMetricsModule',
    'nv.SupportsModule',

    // load attendance modules
    'nv.support-attendance-form',
    'nv.support-overview',
    'nv.my-supports',

    ToolbarsModule,
  ])

  .config(configBlock)
  .run(runBlock).name;

function configBlock(
  $windowProvider,
  $httpProvider,
  $breadcrumbProvider,
  fluxProvider,
  IdleProvider,
  RollbarProvider,
  PublicConfig,
  ROLLBAR_BASE_CONFIG,
  $compileProvider,
  $qProvider,
) {
  $qProvider.errorOnUnhandledRejections(false);

  // Disable Object.freeze on the Flux stores in production mode. This increases speed, but will allow the objects
  // stored in the store to be mutated. We run with Object.freeze ON in DEV_MODE so that we can catch these errors.
  fluxProvider.setImmutableDefaults({ immutable: PublicConfig.DEV_MODE });

  // Get $window from $windowProvider and initialize google analytics account for specific ENV
  const $window = $windowProvider.$get();
  $window.ga('create', PublicConfig.GOOG_ANALTICS_ID, 'auto');

  // parses `x-data-last-updated` header, which refreshes app in case a new version of app is deployed (CM).
  $httpProvider.interceptors.push('PortalLastUpdatedInterceptor');

  $httpProvider.interceptors.push(($q: angular.IQService, AuthenticationRequiredError, $location) => {
    return {
      // tslint:disable-next-line:object-literal-shorthand
      responseError: function(rejection: angular.IHttpResponse<any>) {
        const { data, status } = rejection;

        if (status === 401 && data) {
          const { message } = data;

          if (
            message === API_AUTH_FAILURE_MESSAGE ||
            message === API_AUTH_MISSING_MESSAGE ||
            message === API_AUTH_INVALID_FORMAT
          ) {
            $location.path('/login');
          }
        }

        // We always need to return a rejected promise from a responseError interceptor. If we don't, then Angular
        // treats that as a success response for all future interceptors and promises in the chain.
        return $q.reject(rejection);
      },
    };
  });

  $breadcrumbProvider.setOptions({
    template: require('../shared/directives/breadcrumbs/breadcrumbs.html'),
    prefixStateName: 'main.school.sdc',
  });

  // ng-idle lib
  // 14 minutes for idleness
  IdleProvider.idle(IDLENESS_TIME_TO_SHOW_WARNING);
  // 60 second warning of idleness
  IdleProvider.timeout(IDLENESS_WARNING);
  // after 15 min, user is inactive and will be logged out

  if (PublicConfig.ROLLBAR_POST_CLIENT_ITEM && !$window._KARMA_IS_RUNNING) {
    RollbarProvider.init(ROLLBAR_BASE_CONFIG);
  } else {
    RollbarProvider.deinit();
  }
}

/* istanbul ignore next */
function runBlock(
  $rootScope,
  $state,
  $location: ILocationService,
  AuthenticationRequiredError,
  $mdToast,
  ErrorModal,
  $timeout,
  Auth: Auth,
  Rollbar,
) {
  // kicks off authentication flow (CM).
  Auth.handleAuthentication();

  $rootScope.$state = $state;

  $rootScope.$on('$stateChangeStart', function(event, toState, toParams, fromState) {
    const toStateName = toState && toState.name;
    const fromStateName = fromState && fromState.name;
    const toStateIsPartOfMainState = toStateName && toStateName.includes('main');
    const toStateIsCallback = toStateName === 'callback';
    const fromStateIsNotRoot = fromStateName !== '';

    // Prevent unauthenticated user from accessing `main` or children states of `main` (CM).
    if (toStateIsPartOfMainState && !Auth.isAuthenticated()) {
      event.preventDefault();

      // save url so that user is redirected back to url when authenticated (CM).
      const lastKnownUrl = $location.url();
      return Auth.logout({ lastKnownUrl });
    }

    // Prevent the user from hitting the back button to go back to the `callback` state after
    // just having logged in; Otherwise, this will take them back to Auth0 (CM);
    if (toStateIsCallback && fromStateIsNotRoot) {
      event.preventDefault();
    }

    // adding this to pass params to rootScope immediately on stateChange so
    // they are available for use BEFORE state is loaded.
    // http://stackoverflow.com/questions/21452537/ui-router-use-stateparams-in-service
    $rootScope.toParams = toParams;

    // Reset to no special class on the <html> element
    $rootScope.htmlClass = null;

    $mdToast.hide();
  });

  // on state changes...
  $rootScope.$on('$stateChangeSuccess', function() {
    // use path to return url without any query params
    // use url  to return entire url including any query params
    $rootScope.currentLocation = $location.url();
  });

  $rootScope.$on('$stateChangeError', function(event, toState, toParams, fromState, fromParams, error) {
    const authenticationErrorExists =
      error instanceof AuthenticationRequiredError || error.message === API_AUTH_FAILURE_MESSAGE;
    // we check for error.name instead of instance due to the following transpilation issue:
    // https://stackoverflow.com/questions/42064466/instanceof-using-es6-class-inheritance-chain-doesnt-work
    const authorizationDeniedError = error && error.name === 'AuthorizationDeniedError';
    // Logout user if there is an authentication error (CM).
    if (authenticationErrorExists) {
      const lastKnownUrl = $location.url();
      const errorMessage = 'Unauthorized';
      const authLogoutOpts = { errorMessage, lastKnownUrl };

      Rollbar.debug('AuthenticationRequiredError instance', error);

      return Auth.logout(authLogoutOpts);
    }

    if (authorizationDeniedError) {
      event.preventDefault();

      const fromStateName = fromState.name;
      const toStateName = toState.name;
      const title = 'Unauthorized Access';
      const err = new Error(
        `You cannot access this page because your role does not allow it. ` +
          `Please contact your school leader to request a role change.`,
      );
      err.name = `Unauthorized Access from state "${fromStateName}" to "${toStateName}".`;

      return ErrorModal.show(title, err).then(() => $state.go('main.home'));
    }

    // Fall though for any other state change error
    const errorInfo = {
      message: error.message,
      stack: error.stack,
      data: error.data,
      url: error.config && error.config.url,
    };
    const stateChangeErr = {
      $stateChangeError: {
        toState,
        toParams,
        fromState,
        fromParams,
      },
    };

    _.assign(errorInfo, stateChangeErr);

    ErrorModal.show('A state change error occurred', errorInfo);
    console.error('A state change error occurred: ', errorInfo);
  });

  // on content changes (independent of state)
  $rootScope.$on('$viewContentLoading', function(event, viewConfig) {
    // TODO possible to add progress spinner
  });

  // $rootScope.$on('$viewContentLoaded', function(event) {
  // });
}

export default ng1AppModule;
